// SAB item 5: Soap device-ping client trigger. TELEMETRY, never blocks a money action or
// contest entry. Sends { latitude, longitude } (from the observe-only position cache) to
// the device-ping edge fn, which resolves customer_id server-side and forwards to Soap.
//
// Rules (per the certifier + our constraints):
//   - Fire on contest entry (once per league per session) and before a stale money action.
//   - Send ONLY if a recent cached GPS fix exists. NEVER trigger a fresh prompt.
//   - A missing/stale/failed ping is not fatal; the caller fire-and-forgets.

import { supabase } from '@/integrations/supabase/client';
import { getCachedPosition } from './geoPositionCache';

// A cached fix older than this is not sent (we never prompt to refresh it).
const POSITION_MAX_AGE_MS = 30 * 60 * 1000;
// Re-ping before a money action only when the last ping is older than this.
const PING_STALE_MS = 30 * 60 * 1000;

let lastPingTs = 0;
const pingedLeagues = new Set<string>();

// Minted once per page-load (JS realm). Recorded on every device_ping_events row so two rows
// from the same load share it; the once-per-league-per-session guard is scoped to a load, so
// a differing nonce is what distinguishes a legitimate repeat (a reload) from a guard miss.
const LOAD_NONCE: string =
  (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * Fire-and-forget the device-ping edge fn on a trigger. Fires a diagnostic BEACON on every
 * trigger, even with no cached fix, so "did the client have a fix?" is readable from the EDGE
 * logs with DevTools CLOSED (an open console makes Locance return Noncompliant, contaminating
 * the geo run). The no-fix beacon carries { no_position: true } and the edge fn logs + acks
 * WITHOUT ever calling Soap; only a real cached fix POSTs Soap /device_pings.
 *
 * NO console output here on purpose: nothing must tempt DevTools open during a geo run.
 *
 * Ping-storm guard: lastPingTs advances on EVERY beacon (fix or not), so the 30-min
 * staleness rule in pingBeforeMoneyActionIfStale gates the beacon too — a fixless client
 * doing repeated deposits still beacons at most once per 30 min. Contest entry is separately
 * capped once-per-league-per-session by pingedLeagues below.
 */
type PingTrigger = 'league_entry' | 'money_action';

function sendPing(trigger: PingTrigger, leagueId?: string | null): void {
  lastPingTs = Date.now();
  const pos = getCachedPosition(POSITION_MAX_AGE_MS);
  const base = pos
    ? { latitude: pos.latitude, longitude: pos.longitude }
    : { no_position: true };
  // trigger + league_id make the artifact self-evidencing: which contest the entry control
  // fired for, and (via load_nonce) whether a repeat was a fresh load vs the same one.
  void supabase.functions
    .invoke('device-ping', {
      body: { ...base, trigger, league_id: leagueId ?? null, load_nonce: LOAD_NONCE },
    })
    .catch(() => {
      /* Telemetry: a failed ping is not fatal, and we deliberately stay off the console. */
    });
}

/** Contest entry: ping once per league per session, only if a recent fix is cached. */
export function pingOnLeagueEntry(leagueId: string): void {
  if (pingedLeagues.has(leagueId)) return;
  pingedLeagues.add(leagueId);
  sendPing('league_entry', leagueId);
}

/** Before a money action: re-ping only if the last ping is more than 30 minutes old. */
export function pingBeforeMoneyActionIfStale(leagueId?: string | null): void {
  if (Date.now() - lastPingTs > PING_STALE_MS) sendPing('money_action', leagueId);
}
